import {
  HeaderVariant,
  PartialClickTrackingOptions,
  RegistrationParams,
} from '../types';
import { loswpWorkId } from './workContext';

// Structural subset of the manager's LoginModalEventDetail, which is not
// exported; the full detail object is assignable wherever this is accepted.
export type OpenerDetail = {
  clickTrackingContext?: PartialClickTrackingOptions;
  registrationParams?: RegistrationParams;
  headerVariant?: HeaderVariant;
  workTitle?: string;
};

export type OpenerIntent =
  | 'download'
  | 'read'
  | 'save'
  | 'share'
  | 'cite'
  | 'follow'
  | 'message'
  | 'signup'
  | 'other';

// Every clickTrackingContext.source dispatched through the manager, from the
// full opener sweep of 2026-07-21. The same source string can arrive with
// either headerVariant (work-card variants share sources), so intent is the
// only thing this table may decide — modal version always reads the detail.
const INTENT_BY_SOURCE: Record<string, OpenerIntent> = {
  'loswp--download-pdf-button--sticky-ctas': 'download',
  'loswp--download-pdf-button--work-card': 'download',
  'loswp--download-pdf-button--ungated-work-card': 'download',
  'loswp--wsj-grid-card-download-pdf-modal': 'download',
  'loswp--related-papers-carousel-download-pdf-modal': 'download',
  'loswp--interstitial-related-papers-download': 'download',
  'loswp-ungate-desktop-download': 'download',
  'loswp-ungate-floating-badge-download': 'download',
  'loswp-ungate-sticky-download': 'download',
  'loswp--continue-reading-button--sticky-ctas': 'read',
  'loswp--continue-reading-button--work-card': 'read',
  'loswp--continue-reading-button--ungated-work-card': 'read',
  'loswp-ungate-sticky-see-pdf': 'read',
  'loswp--swp-splash-paper-cover': 'read',
  'loswp-gate-overlay': 'read',
  'loswp--bookmark-button--work-card': 'save',
  'loswp-ungate-desktop-save': 'save',
  'loswp-ungate-sticky-save': 'save',
  'save-button-login-modal--aei-figure-card': 'save',
  'loswp--share-button--work-card': 'share',
  'loswp-ungate-desktop-share': 'share',
  'loswp-ungate-sticky-share': 'share',
  'share-button-login-modal--aei-figure-card': 'share',
  'cite-button-login-modal--aei-figure-card': 'cite',
  'loswp--related-topics--follow': 'follow',
  'loswp--signup-banner': 'signup',
  cited_today_welcome_modal: 'signup',
  'citeright.upload_login_modal': 'other',
};

const INTENT_BY_SOURCE_PREFIX: ReadonlyArray<[string, OpenerIntent]> = [
  ['loswp-about-authors-follow-', 'follow'],
  ['loswp-about-authors-message-', 'message'],
  ['topics--', 'follow'],
  ['cited_today_welcome_header_', 'signup'],
];

export function openerIntent(detail: OpenerDetail): OpenerIntent {
  const source = detail.clickTrackingContext?.source;
  if (!source) return 'other';

  const exact = INTENT_BY_SOURCE[source];
  if (exact) return exact;

  const prefixed = INTENT_BY_SOURCE_PREFIX.find(([prefix]) =>
    source.startsWith(prefix),
  );
  return prefixed ? prefixed[1] : 'other';
}

export function modalVersion(detail: OpenerDetail): 'paper_card' | 'default' {
  return detail.headerVariant === 'yourPaperCard' ? 'paper_card' : 'default';
}

export function isDownloadRead(detail: OpenerDetail): boolean {
  const intent = openerIntent(detail);
  return intent === 'download' || intent === 'read';
}

export function isWorkAnchored(detail: OpenerDetail): boolean {
  return Boolean(detail.workTitle) || loswpWorkId() !== null;
}

export function isMobileViewport(): boolean {
  return window.matchMedia('(max-width: 767px)').matches;
}
